import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtRange, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Loader2, Download, Link2, Trash2, Copy, CreditCard, Eye, EyeOff, ArrowLeft, X, Mail,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import ActivityTimeline from "@/components/proposal/ActivityTimeline";

const STATUSES = ["DRAFT", "READY", "SENT", "VIEWED", "ACCEPTED", "DECLINED", "EXPIRED"];

export default function ProposalDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [internal, setInternal] = useState(true);
  const [busy, setBusy] = useState(false);
  const [client, setClient] = useState({ name: "", email: "", company: "" });
  const [discount, setDiscount] = useState(0);
  const [clientNotes, setClientNotes] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [sendOpen, setSendOpen] = useState(false);
  const [sendForm, setSendForm] = useState({ recipient: "", message: "", include_pdf: true });
  const [sending, setSending] = useState(false);

  const load = () => api.get(`/proposals/${id}`).then(({ data }) => {
    setP(data);
    setClient(data.client || {});
    setDiscount(data.discount_pct || 0);
    setClientNotes(data.client_notes || "");
    setInternalNotes(data.internal_notes || "");
  }).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const body = { client, discount_pct: Number(discount) || 0, client_notes: clientNotes, internal_notes: internalNotes, ...extra };
      const { data } = await api.put(`/proposals/${id}`, body);
      setP(data);
      toast.success("Saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeItem = async (idx) => {
    const items = (p.line_items || []).filter((_, i) => i !== idx);
    await save({ line_items: items });
  };

  const downloadPdf = async (mode) => {
    try {
      const res = await api.get(`/proposals/${id}/pdf?mode=${mode}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `proposal-${mode}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not generate PDF"); }
  };

  const copyShare = () => {
    const link = `${window.location.origin}/p/${p.share_token}`;
    navigator.clipboard.writeText(link);
    toast.success("Client link copied");
  };

  const checkout = async (mode) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { proposal_id: id, origin_url: window.location.origin, mode });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };

  const openSend = () => {
    setSendForm({ recipient: p.client?.email || "", message: p.client_notes || "", include_pdf: true });
    setSendOpen(true);
  };

  const sendEmail = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sendForm.recipient)) { toast.error("Enter a valid recipient email."); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/proposals/${id}/send-email`, {
        recipient: sendForm.recipient, message: sendForm.message,
        include_pdf: sendForm.include_pdf, origin_url: window.location.origin,
      });
      if (data.sent) { toast.success(`Proposal sent to ${data.recipient}`); setSendOpen(false); load(); }
      else { toast.warning(data.detail || "Could not send email."); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!p) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  const tot = p.totals || {};
  const one = tot.one_time || {};
  const cur = tot.currency || "USD";
  const items = p.line_items || [];
  // Internal commercial data (cost/margin/hours/audit/discount) is operator-only. The server
  // enforces this (is_operator_view); the UI only renders what the server actually returned.
  const isOperator = !!p.is_operator_view;
  const showInternal = isOperator && internal;

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8" data-testid="proposal-detail">
      <button onClick={() => nav("/proposals")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-3"><ArrowLeft className="w-4 h-4" /> All proposals</button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <h1 className="font-heading text-2xl font-bold text-slate-900">{p.title}</h1>
          <p className="text-slate-500 text-sm">{p.business_name} {p.business_address ? `· ${p.business_address}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase text-slate-600">v{p.version}</span>
          {isOperator && (
            <button onClick={() => setInternal((v) => !v)} data-testid="view-toggle"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {internal ? <><Eye className="w-4 h-4" /> Internal view</> : <><EyeOff className="w-4 h-4" /> Client view</>}
            </button>
          )}
        </div>
      </div>

      {/* Status + actions */}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {isOperator ? (
          <select value={p.status} onChange={(e) => save({ status: e.target.value })} data-testid="proposal-status"
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        ) : (
          <span data-testid="proposal-status-badge" className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{p.status}</span>
        )}
        {isOperator && <Button variant="outline" size="sm" onClick={copyShare} data-testid="proposal-share"><Link2 className="w-4 h-4 mr-1.5" /> Client link</Button>}
        {isOperator && <Button variant="outline" size="sm" onClick={openSend} data-testid="proposal-send-email"><Mail className="w-4 h-4 mr-1.5" /> Send to client</Button>}
        <Button variant="outline" size="sm" onClick={() => downloadPdf("client")} data-testid="proposal-pdf-client"><Download className="w-4 h-4 mr-1.5" /> {isOperator ? "Client PDF" : "Download PDF"}</Button>
        {isOperator && <Button variant="outline" size="sm" onClick={() => downloadPdf("internal")} data-testid="proposal-pdf-internal"><Download className="w-4 h-4 mr-1.5" /> Internal PDF</Button>}
        {isOperator && <Button variant="outline" size="sm" onClick={async () => { const { data } = await api.post(`/proposals/${id}/duplicate`); nav(`/proposals/${data.public_id}`); }} data-testid="proposal-duplicate"><Copy className="w-4 h-4 mr-1.5" /> Duplicate</Button>}
        <Button variant="outline" size="sm" onClick={async () => { if (window.confirm("Delete this proposal?")) { await api.delete(`/proposals/${id}`); nav("/proposals"); } }} data-testid="proposal-delete" className="text-red-600 border-red-200"><Trash2 className="w-4 h-4 mr-1.5" /> Delete</Button>
      </div>

      {/* Line items */}
      <div className="mt-6 space-y-2" data-testid="proposal-line-items">
        {items.length === 0 && <p className="text-sm text-slate-400">No solutions in this proposal yet. Add some from a scan's solutions page.</p>}
        {items.map((li, i) => {
          const pr = li.pricing || {};
          return (
            <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`line-item-${i}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-slate-900 flex items-center gap-2">{li.name}
                    {li.needs_review && <span data-testid={`line-provisional-${i}`} className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-300">Provisional</span>}
                  </p>
                  <p className="text-xs text-slate-500">{li.timeframe} · {li.priority}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <div className="text-right">
                    <div className="font-semibold text-slate-900">{fmtRange(pr.price_range, cur)}</div>
                    {pr.recurring && <div className="text-xs text-slate-500">+{fmtMoney(pr.recurring.client_price, cur)}/mo</div>}
                  </div>
                  <button onClick={() => removeItem(i)} data-testid={`remove-item-${i}`} className="text-slate-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                </div>
              </div>
              {showInternal && (
                <div className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-2 border-t border-slate-100 pt-2 text-xs text-slate-500" data-testid={`line-internal-${i}`}>
                  <div>Delivery cost: <b className="text-slate-700">{fmtMoney(pr.delivery_cost, cur)}</b></div>
                  <div>Gross profit: <b className="text-slate-700">{fmtMoney(pr.gross_profit, cur)}</b></div>
                  <div>Margin: <b className="text-slate-700">{Math.round((pr.gross_margin || 0) * 100)}%</b></div>
                  <div>Hours: <b className="text-slate-700">{pr.total_hours}h</b> ({fmtMoney(pr.effective_hourly, cur)}/h)</div>
                </div>
              )}
              {showInternal && li.scope_audit && (
                <details className="mt-2 text-xs" data-testid={`line-audit-${i}`}>
                  <summary className="cursor-pointer font-semibold text-indigo-600">Pricing audit trail (internal)</summary>
                  <div className="mt-2 space-y-1.5 rounded-lg bg-slate-50 border border-slate-200 p-3 text-slate-600">
                    <div><b>Findings → solution:</b> {(li.scope_audit.findings || []).map((f) => `${f.code || f.issue} (${f.severity})`).join(", ")} → {li.scope_audit.recommended_solution?.name}</div>
                    <div><b>Baseline hours:</b> {li.scope_audit.hours?.baseline?.breakdown}</div>
                    {li.scope_audit.hours?.ai_refinement?.available ? (
                      <div><b>AI-refined hours:</b> {li.scope_audit.hours.ai_refinement.ai_hours_raw}h → applied {li.scope_audit.hours.ai_refinement.applied_hours}h (conf: {li.scope_audit.hours.ai_refinement.confidence}{li.scope_audit.hours.ai_refinement.clamped_to_bounds ? ", clamped to bounds" : ""}). <span className="italic">{li.scope_audit.hours.ai_refinement.reasoning}</span></div>
                    ) : (
                      <div><b>AI refinement:</b> {li.scope_audit.hours?.ai_refinement?.note || "not applied — deterministic baseline used"}</div>
                    )}
                    <div><b>Labor type:</b> {li.scope_audit.hours?.labor_type} · applied {li.scope_audit.hours?.applied_hours}h [{li.scope_audit.hours?.status}]</div>
                    <div><b>Delivery cost:</b> {li.scope_audit.delivery_cost?.calc}</div>
                    <div><b>Pricing rule:</b> {li.scope_audit.pricing_rule?.client_price_calc}</div>
                    <div><b>Gross profit / margin:</b> {li.scope_audit.pricing_rule?.gross_profit_calc}; {li.scope_audit.pricing_rule?.gross_margin_calc}</div>
                    <div><b>Final client price:</b> {fmtMoney(li.scope_audit.final_client_price, cur)}</div>
                    {li.needs_review && (li.scope_audit.review_reasons || []).length > 0 && (
                      <div className="text-amber-700"><b>Needs review:</b> {li.scope_audit.review_reasons.join(" ")}</div>
                    )}
                  </div>
                </details>
              )}
            </div>
          );
        })}
      </div>

      {/* Totals */}
      <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-5" data-testid="proposal-totals">
        <div className="flex items-center justify-between">
          <span className="text-slate-600">One-time investment</span>
          <span className="text-xl font-bold text-slate-900">{fmtMoney(one.client_price, cur)}</span>
        </div>
        {one.client_price_before_discount && (
          <div className="flex items-center justify-between text-sm text-slate-400">
            <span>Before {one.discount_pct}% discount</span><span className="line-through">{fmtMoney(one.client_price_before_discount, cur)}</span>
          </div>
        )}
        {tot.recurring && (
          <div className="flex items-center justify-between text-sm text-slate-600 mt-1">
            <span>Ongoing management</span><span className="font-semibold">{fmtMoney(tot.recurring.client_price, cur)}/mo</span>
          </div>
        )}
        {showInternal && (
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 border-t border-slate-200 pt-3 text-xs" data-testid="totals-internal">
            <div className="text-slate-500">Delivery cost: <b className="text-slate-800">{fmtMoney(one.delivery_cost, cur)}</b></div>
            <div className="text-slate-500">Gross profit: <b className="text-slate-800">{fmtMoney(one.gross_profit, cur)}</b></div>
            <div className="text-slate-500">Gross margin: <b className="text-slate-800">{Math.round((one.gross_margin || 0) * 100)}%</b></div>
            <div className="text-slate-500">Total hours: <b className="text-slate-800">{one.hours}h</b></div>
          </div>
        )}
      </div>

      {/* Checkout */}
      <div className="mt-4 flex flex-wrap gap-2">
        <Button onClick={() => checkout("deposit")} disabled={busy || !one.client_price} data-testid="checkout-deposit" className="bg-emerald-600 hover:bg-emerald-700">
          <CreditCard className="w-4 h-4 mr-2" /> Pay 50% deposit ({fmtMoney((one.client_price || 0) / 2, cur)})
        </Button>
        <Button onClick={() => checkout("full")} disabled={busy || !one.client_price} variant="outline" data-testid="checkout-full">
          Pay in full ({fmtMoney(one.client_price, cur)})
        </Button>
      </div>

      {/* Editable fields — operator/agency sales management only */}
      {isOperator && (
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div>
          <h3 className="font-heading font-semibold text-slate-900 text-sm mb-2">Client</h3>
          <div className="space-y-2">
            <Input placeholder="Client name" value={client.name || ""} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="client-name" />
            <Input placeholder="Client email" value={client.email || ""} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="client-email" />
            <Input placeholder="Company" value={client.company || ""} onChange={(e) => setClient({ ...client, company: e.target.value })} data-testid="client-company" />
          </div>
        </div>
        <div>
          <h3 className="font-heading font-semibold text-slate-900 text-sm mb-2">Pricing & notes</h3>
          <div className="space-y-2">
            <label className="block text-xs text-slate-500">Discount % (authorized)
              <Input type="number" min="0" max="90" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="discount-input" /></label>
            <textarea placeholder="Client-facing notes / executive summary" value={clientNotes} onChange={(e) => setClientNotes(e.target.value)} data-testid="client-notes"
              className="w-full rounded-lg border border-slate-200 p-2 text-sm" rows={2} />
            <textarea placeholder="Internal notes (never shown to client)" value={internalNotes} onChange={(e) => setInternalNotes(e.target.value)} data-testid="internal-notes"
              className="w-full rounded-lg border border-slate-200 p-2 text-sm" rows={2} />
          </div>
        </div>
      </div>
      )}

      {isOperator && (
      <div className="mt-4">
        <Button onClick={() => save()} disabled={busy} data-testid="proposal-save" className="bg-slate-900 hover:bg-slate-800">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save changes"}
        </Button>
      </div>
      )}

      <p className="mt-6 text-xs text-slate-400">Prices are AI-assisted estimates from configurable pricing rules, not market-verified prices. Modeled opportunity figures are estimates of potential upside, not guaranteed revenue, savings or ROI.</p>

      {isOperator && <ActivityTimeline id={id} />}

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle>Send proposal to client</DialogTitle>
            <DialogDescription>
              We'll email a secure link to the client-facing proposal (and optionally the PDF). Internal cost, margin and notes are never included.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-slate-500">Recipient email</label>
              <Input type="email" placeholder="client@business.com" value={sendForm.recipient}
                onChange={(e) => setSendForm({ ...sendForm, recipient: e.target.value })} data-testid="send-recipient" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-500">Message (optional)</label>
              <textarea rows={3} value={sendForm.message} onChange={(e) => setSendForm({ ...sendForm, message: e.target.value })}
                data-testid="send-message" className="w-full rounded-lg border border-slate-200 p-2 text-sm"
                placeholder="Add a short note for your client…" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" checked={sendForm.include_pdf} onChange={(e) => setSendForm({ ...sendForm, include_pdf: e.target.checked })}
                data-testid="send-include-pdf" className="w-4 h-4 accent-indigo-600" />
              Attach client PDF
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)} data-testid="send-cancel">Cancel</Button>
            <Button onClick={sendEmail} disabled={sending} data-testid="send-submit" className="bg-slate-900 hover:bg-slate-800">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" /> Send proposal</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
